/**
 * Defines sagas related to Primary Navigation bar feature.
 */

import {
  put, call, delay, select,
} from 'redux-saga/effects';
import {
  RETRY_DELAY, REDIRECTING_TO_LOGIN, USER_IMPERSONATION_UNAUTHORISED_ERROR, USER_IMPERSONATION_ERROR, SIGN_IN_AS_GUID,
  GET_CUSTOM_TOKEN_ERROR, USER_IMPERSONATION_BROADCAST_TYPE,
} from 'constants/AppConstants';
import {
  getAccessTokenService, getNewAccessTokenService, createUserInAMP, getUserOnboardingStatus, validateUserImpersonationService, expireGuid,
} from 'services/App.service';
import {
  setAuthToken, redirectToOkta, getRedirectUrl, calculateExpiryTime, getTokenExpiryTime,
  getTimeoutWindowMinutes, signOutUser, getSubRoute, parseJwt, getAuthToken, isShareToken, getShareGuid,
  signOutGuestToSignIn,
} from 'utils/app.utils';
import * as types from 'constants/ActionTypes';
import { getMfaConfigAction, getUserDetails, getLoaders } from 'features/side-bar/SideBar.actions';
import get from 'lodash/get';
import sysend from 'sysend';
import * as authAction from './Authorize.actions';

export function* getAccessTokenSaga(action) {
  try {
    const { code, state } = action;
    const response = yield call(getAccessTokenService, code, getRedirectUrl());
    if (response.errorCode) {
      throw Error(response.errorDescription);
    }
    const expiresIn = response && response.expires_in;
    const expiryTime = calculateExpiryTime(expiresIn);
    setAuthToken({ ...response, expiry_time: expiryTime });
    yield put({ type: types.SET_IS_SHARE_TOKEN, payload: isShareToken(response.access_token) });
    const subRoute = getSubRoute(state);

    const jwtToken = parseJwt(response.access_token);
    const autoProvision = yield select((s) => s.Authorize.autoProvisionFlag);

    if (jwtToken.organizationId) {
      yield put({ type: types.OKTA_TOKEN_RESPONDED });
      window.location.assign(subRoute);
    } else if (!autoProvision) {
      yield put(authAction.addUserInAMP(jwtToken.userId, subRoute));
      yield put({ type: types.OKTA_TOKEN_RESPONDED });
    } else {
      yield put(authAction.setAutoProvisionStatus('PENDING'));
    }
  } catch (error) {
    redirectToOkta();
  }
}

export function* addUserInAMPSaga(action) {
  try {
    const { userName, subRoute } = action;
    const response = yield call(createUserInAMP, userName);
    if (!response.ok) {
      throw Error(response.json.errorDescription);
    }
    yield put(authAction.getNewAccessTokenAction(true, subRoute));
  } catch (error) {
    redirectToOkta();
  }
}

const handleAccessTokenErrorSaga = (response) => {
  if (!response.ok) {
    const error = new Error(response.json.errorDescription ? response.json.errorDescription : '');
    error.errorCode = response.json.errorCode ? response.json.errorCode : 0;
    error.statusCode = response.status;
    throw error;
  }
};

const extractAndSetTokenDetails = (response) => {
  const tokenDetails = response.json;
  const expiresIn = tokenDetails && tokenDetails.expires_in;
  const expiryTime = calculateExpiryTime(expiresIn);
  setAuthToken({ ...tokenDetails, expiry_time: expiryTime });
};

export function* handleTerminateImpersonationSaga({ errMsg = '' }) {
  try {
    // Remove guid from LS, fetch new token for primary user session and redirect to home page
    localStorage.removeItem(SIGN_IN_AS_GUID);
    const redirectUrl = window.location.origin;
    const response = yield call(getNewAccessTokenService, redirectUrl, '');
    handleAccessTokenErrorSaga(response);
    if (errMsg) {
      yield put({ type: types.OKTA_NEW_TOKEN_FAILURE, retryCount: 0, error: new Error(errMsg) });
    }
    extractAndSetTokenDetails(response);
    yield put({ type: types.SET_IS_SHARE_TOKEN, payload: isShareToken(get(response, 'json.access_token', '')) });
    // broadcast user impersonation event to show reload dialog in other tabs when secondary user session is terminated
    yield call(sysend.broadcast, USER_IMPERSONATION_BROADCAST_TYPE);
    window.location.assign('/');
  } catch (error) {
    // Logout user if error occurs while fetching new token for primary user session
    error.message = REDIRECTING_TO_LOGIN;
    yield put({ type: types.OKTA_NEW_TOKEN_FAILURE, retryCount: 0, error });
    if (error.statusCode !== 401) { // since 401 is handled by interceptor, we need to signout user for other errors
      yield put({ type: types.SET_IS_SHARE_TOKEN, payload: false });
      signOutUser();
    }
  }
}

export function* handleResetErrorSaga(error) {
  if (error.statusCode !== 401) {
    const retryCount = yield select((state) => state.Authorize.retryCount);
    if (retryCount < 3) {
      yield put({ type: types.OKTA_NEW_TOKEN_FAILURE, retryCount: retryCount + 1, error });
      yield delay(RETRY_DELAY[retryCount + 1]);
      yield put({ type: types.OKTA_NEW_TOKEN_REQUESTED });
    } else {
      // if guid is present in LS, then terminate user impersonation and switch to primary user session
      // eslint-disable-next-line no-lonely-if
      if (localStorage.getItem(SIGN_IN_AS_GUID)) {
        yield call(handleTerminateImpersonationSaga, { errMsg: GET_CUSTOM_TOKEN_ERROR });
        return;
      }
      // — Guest user: no Okta session; redirect to app sign-in instead of Okta.
      if (isShareToken(getAuthToken())) {
        yield put({ type: types.SET_IS_SHARE_TOKEN, payload: false });
        signOutGuestToSignIn(window.location.pathname + window.location.search);
        return;
      }
      yield put({ type: types.OKTA_NEW_TOKEN_FAILURE, retryCount: 0, error: new Error(REDIRECTING_TO_LOGIN) });
      yield put({ type: types.SET_IS_SHARE_TOKEN, payload: false });
      signOutUser();
    }
  }
}

const addSubRoute = (isNewUserCreated, subRoute) => {
  if (isNewUserCreated) {
    window.location.assign(subRoute);
  }
};

export function* getNewAccessTokenSaga(action) {
  try {
    const { isNewUserCreated, subRoute } = action;
    const retryCount = yield select((state) => state.Authorize.retryCount);
    const expiryTimeInLocalStorage = getTokenExpiryTime();
    if (expiryTimeInLocalStorage) {
      const oldTokenExpiryTime = new Date(expiryTimeInLocalStorage);
      const currDate = new Date();
      if (oldTokenExpiryTime >= new Date(currDate.getTime() + (getTimeoutWindowMinutes() * 60 * 1000)) && !isNewUserCreated) {
        yield put({ type: types.RESET_TIMER, reset: true });
      } else if (oldTokenExpiryTime < currDate && retryCount === 0) {
        yield put({ type: types.RESET_TIMER, reset: false });
      } else {
        const redirectUrl = window.location.origin;
        const accessToken = getAuthToken();
        // Impersonation and guest are mutually exclusive: pass only one to refresh. #genai
        const impersonationGuid = localStorage.getItem(SIGN_IN_AS_GUID) || '';
        const shareGuid = !impersonationGuid && isShareToken(accessToken) ? getShareGuid(accessToken) : '';
        const response = yield call(getNewAccessTokenService, redirectUrl, impersonationGuid, shareGuid);
        handleAccessTokenErrorSaga(response);
        extractAndSetTokenDetails(response);
        yield put({ type: types.SET_IS_SHARE_TOKEN, payload: isShareToken(get(response, 'json.access_token', '')) });
        addSubRoute(isNewUserCreated, subRoute);
      }
      yield put({ type: types.RESET_RETRY_COUNT });
    }
  } catch (error) {
    // 401 handling: impersonation and guest are separate paths (interceptor skips 401 when impersonating). #genai
    if (error.statusCode === 401 && localStorage.getItem(SIGN_IN_AS_GUID)) {
      yield call(handleTerminateImpersonationSaga, { errMsg: GET_CUSTOM_TOKEN_ERROR });
      return;
    }
    if (error.statusCode === 401 && isShareToken(getAuthToken())) {
      yield put({ type: types.SET_IS_SHARE_TOKEN, payload: false });
      signOutGuestToSignIn(window.location.pathname + window.location.search);
      return;
    }
    yield call(handleResetErrorSaga, error);
  }
}

export function* getUserOnboardingStatusSaga() {
  try {
    const response = yield call(getUserOnboardingStatus);
    if (!response.ok) {
      throw Error(response.json.errorDescription);
    }
    yield put({ type: types.GET_USER_ONBOARDING_STATUS_SUCCESS, userStatus: response.json });
  } catch (error) {
    yield put({ type: types.SHOW_CONTAINER_TOAST, error });
  }
}

export function* getCustomAccessTokenSaga({ impersonationGuid }) {
  try {
    const redirectUrl = window.location.origin;
    const response = yield call(getNewAccessTokenService, redirectUrl, impersonationGuid);
    if (!response.ok) {
      const error = new Error((response.status === 400 || response.status === 401) ? USER_IMPERSONATION_UNAUTHORISED_ERROR
        : USER_IMPERSONATION_ERROR);
      error.statusCode = response.status;
      throw error;
    }
    extractAndSetTokenDetails(response);
    yield put({ type: types.SET_IS_SHARE_TOKEN, payload: isShareToken(get(response, 'json.access_token', '')) });
    // broadcast user impersonation event to show reload dialog in other tabs
    yield call(sysend.broadcast, USER_IMPERSONATION_BROADCAST_TYPE);
    window.location.assign('/');
  } catch (error) {
    localStorage.removeItem(SIGN_IN_AS_GUID);
    yield put({ type: types.OKTA_NEW_TOKEN_FAILURE, retryCount: 0, error });
  }
}

export function* validateUserImpersonationSaga({ impersonationGuid }) {
  try {
    const response = yield call(validateUserImpersonationService, impersonationGuid);
    if (!response.ok || (response.ok && !get(response, 'json.isActive', false))) {
      const error = new Error(GET_CUSTOM_TOKEN_ERROR);
      throw error;
    }
    yield put(getMfaConfigAction());
    yield put(getUserDetails(true));
    yield put(getLoaders());
  } catch (error) {
    yield put({ type: types.OKTA_NEW_TOKEN_FAILURE, retryCount: 0, error });
    // delay primary token fetch and refresh when guid is expired so that the toast message is visible for 3 seconds before refresh
    yield delay(3000);
    yield call(handleTerminateImpersonationSaga, {});
  }
}

export function* expireGuidSaga() {
  try {
    const response = yield call(expireGuid, localStorage.getItem(SIGN_IN_AS_GUID));
    if (!response.ok) {
      throw Error(response.json.errorDescription);
    }
    yield call(handleTerminateImpersonationSaga, {});
  } catch (error) {
    yield put({ type: types.SHOW_CONTAINER_TOAST, error });
    yield put({ type: types.SET_SIGN_OUT_DISABLED, payload: false });
  }
}
